import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import UserManagement from './components/UserManagement';
import AdminSettings from './components/AdminSettings';
import UnauthorizedUser from './components/UnauthorizedUser';
import ProductManagement from './components/ProductManagement';
import ScanHistory from './components/ScanHistory';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Handle OAuth callback
const OAuthCallback: React.FC = () => {
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);
  const [unauthorizedEmail, setUnauthorizedEmail] = React.useState<string>('');
  const [errorType, setErrorType] = React.useState<'unauthorized' | 'deactivated'>('unauthorized');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for unauthorized user parameters first
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const email = urlParams.get('email');

        if ((error === 'unauthorized' || error === 'deactivated') && email) {
          setIsUnauthorized(true);
          setUnauthorizedEmail(email);
          setErrorType(error as 'unauthorized' | 'deactivated');
          return;
        }

        // Try to get data from URL parameters
        const accessToken = urlParams.get('access_token');
        const userDataParam = urlParams.get('user');

        if (accessToken && userDataParam) {
          try {
            const userData = JSON.parse(decodeURIComponent(userDataParam));
            console.log('OAuth callback - Storing user data:', userData);
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Verify data was stored
            const storedToken = localStorage.getItem('access_token');
            const storedUser = localStorage.getItem('user');
            console.log('OAuth callback - Verification:', {
              tokenStored: !!storedToken,
              userStored: !!storedUser,
              userAgent: navigator.userAgent
            });
            
            // For mobile browsers, use replace instead of href to avoid back button issues
            if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
              console.log('Mobile browser - redirecting to dashboard with replace');
              window.location.replace('/dashboard');
            } else {
              console.log('Desktop browser - redirecting to dashboard with href');
              window.location.href = '/dashboard';
            }
            return;
          } catch (error) {
            console.error('Failed to parse user data:', error);
          }
        }

        // If no URL parameters, try to get data from the current page content
        // This handles cases where the backend returns JSON directly
        const response = await fetch(window.location.href);
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.access_token && data.user) {
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('user', JSON.stringify(data.user));
            
            // For mobile browsers, use replace instead of href to avoid back button issues
            if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
              window.location.replace('/dashboard');
            } else {
              window.location.href = '/dashboard';
            }
            return;
          }
        }

        // If we can't find the data, redirect to login
        window.location.href = '/login';
      } catch (error) {
        console.error('OAuth callback error:', error);
        window.location.href = '/login';
      }
    };

    handleCallback();
  }, []);

  // Show unauthorized user page if needed
  if (isUnauthorized) {
    return <UnauthorizedUser email={unauthorizedEmail} errorType={errorType} />;
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.50'
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Processing authentication...
        </Typography>
      </Box>
    </Box>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ 
  children, 
  adminOnly = false 
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.50'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" replace />} />
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute adminOnly>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute adminOnly>
            <AdminSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute adminOnly>
            <div>Analytics Page - Coming Soon</div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/products" 
        element={
          <ProtectedRoute adminOnly>
            <ProductManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scan-history" 
        element={
          <ProtectedRoute>
            <ScanHistory />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
